import { useEffect, useRef, useState } from 'react';
import { Storage } from '../lib/storage';
import { playerSurfaceActive } from '../lib/api/customerContext';
import {
  DEFAULT_REPORTING_SETTINGS,
  REPORTING_SETTINGS_KEY,
  type ReportingSettings,
} from '../engine/reporting';

// ─────────────────────────────────────────────────────────────────────
// REPORTING SETTINGS — the platform fee model (percentage per transaction +
// fixed monthly fee) and the demo-row switch used by the Reporting module.
//
// Same flag-guarded pattern as the other persisted slices (§13.20): load once
// on mount, never write before the first read has completed, and Storage-first
// on save (`await Storage.set` BEFORE `setState`).
// ─────────────────────────────────────────────────────────────────────

export function useReportingSettings() {
  const [settings, setSettings] = useState<ReportingSettings>(DEFAULT_REPORTING_SETTINGS);
  const loaded = useRef(false);

  useEffect(() => {
    // Club configuration: not a member's to read, and a 401 ends their
    // session (#393).
    if (playerSurfaceActive()) return undefined;
    (async () => {
      try {
        const res = await Storage.get(REPORTING_SETTINGS_KEY);
        if (res?.value) {
          const parsed = JSON.parse(res.value) as Partial<ReportingSettings>;
          setSettings((prev) => sanitize({ ...prev, ...parsed }));
        }
      } catch (e) {
        console.error('[Reporting] Kunne ikke laste innstillinger:', e);
      } finally {
        loaded.current = true;
      }
    })();
  }, []);

  /** Persist then apply. Rejects (and leaves state untouched) if Storage fails. */
  const saveSettings = async (next: Partial<ReportingSettings>) => {
    const merged = sanitize({ ...settings, ...next });
    // Club configuration is not a member's to write, and the 401 would end
    // their session (#393). Returns the merged value so the signature is
    // unchanged — nothing on the member surface reaches this anyway.
    if (playerSurfaceActive()) return merged;
    await Storage.set(REPORTING_SETTINGS_KEY, JSON.stringify(merged));
    setSettings(merged);
    return merged;
  };

  return { settings, saveSettings, settingsLoaded: loaded };
}

/** Clamp to sane values — a fee can be 0 but never negative or NaN. */
export function sanitize(s: ReportingSettings): ReportingSettings {
  const pct = Number(s.feePct);
  const fixed = Number(s.monthlyFeeNok);
  return {
    feePct: Number.isFinite(pct)
      ? Math.min(100, Math.max(0, pct))
      : DEFAULT_REPORTING_SETTINGS.feePct,
    monthlyFeeNok: Number.isFinite(fixed)
      ? Math.max(0, Math.round(fixed))
      : DEFAULT_REPORTING_SETTINGS.monthlyFeeNok,
    includeDemo: Boolean(s.includeDemo),
  };
}
